import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import { Button as GradientButton } from "@ui/index";

import { DescriptionTypography } from '@/components/LoginDialog/components';
import { MainTextTypography } from '@/components/RecoveryDialog/components';
import Form from '@/components/RecoveryDialog/Form';
import { ERecovery } from '@/layouts/SecondRootLayout';
import { validateResetToken } from '@/store/actions/user';
import { useAppDispatchUnwrap } from '@/store/hooks';

interface IThirdStep {
  handleNextStep: () => void;
  resetToken: string | null;
  /** Routes the user back to FirstStep (enter-email) so they can request a
   *  fresh reset link. Used by the "invalid link" state's CTA. Wired by
   *  LoginDialog as setAuthPanel(2). */
  onRequestNewLink?: () => void;
  /** Optional remeasure hook from LoginDialog. Called when the internal
   *  validation state flips (checking → valid / invalid) so the slider's
   *  viewport height grows / shrinks to fit the new content immediately
   *  instead of clipping while the ResizeObserver catches up. Mirrors
   *  the pattern the login Form already uses. */
  onLayoutChange?: () => void;
}

type ValidationState = "checking" | "valid" | "invalid";

const ThirdStep: FC<IThirdStep> = ({ handleNextStep, resetToken, onRequestNewLink, onLayoutChange }) => {
  const { t } = useTranslation('login', {
    keyPrefix: 'passwordRecovery.thirdStep',
  });

  // Non-consuming pre-flight check. Without this, users only learn the
  // link is dead (already used / replaced / expired) AFTER typing a new
  // password and submitting — confusing UX. The validate endpoint reads
  // the JTI from the DB but doesn't mutate, so a valid token stays valid
  // for the subsequent PATCH /reset-password call.
  const [state, setState] = useState<ValidationState>("checking");
  const dispatchUnwrap = useAppDispatchUnwrap();

  useEffect(() => {
    if (!resetToken) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatchUnwrap(validateResetToken(resetToken));
        if (cancelled) return;
        setState(res?.valid ? "valid" : "invalid");
      } catch {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => { cancelled = true; };
    // dispatchUnwrap is a new function every render (see store/hooks.ts);
    // depending only on `resetToken` keeps this from refiring on every
    // parent re-render, which would spam the validate endpoint.
    // eslint-disable-next-line
  }, [resetToken]);

  // Force the parent slider to remeasure whenever the rendered content
  // swaps between the three states. The ResizeObserver in LoginDialog
  // catches this eventually but with a flash of clipped content; this
  // hook fires immediately on commit so the modal grows / shrinks cleanly.
  useEffect(() => {
    onLayoutChange?.();
  }, [state, onLayoutChange]);

  return (
    <Stack
      spacing="24px"
      width="100%"
      justifyContent="center"
      alignItems="center"
    >
      <Stack spacing="24px" width="100%" justifyContent="center" alignItems="center">
        {state === "invalid" ? (
          <>
            <MainTextTypography>This link is no longer valid</MainTextTypography>
            <div style={{
              width: "100%",
              padding: "16px 20px",
              background: "rgba(235, 194, 142, 0.14)",
              border: "1px solid rgba(235, 194, 142, 0.4)",
              borderRadius: "10px",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}>
              <DescriptionTypography>
                Your password reset link has expired or has already been used. Request a new one to continue.
              </DescriptionTypography>
            </div>
          </>
        ) : (
          <>
            <MainTextTypography>{t('mainText')}</MainTextTypography>
            <DescriptionTypography>
              <Trans t={t} i18nKey="description" />
            </DescriptionTypography>
          </>
        )}
      </Stack>

      {state === "checking" && (
        <DescriptionTypography>Checking your link…</DescriptionTypography>
      )}

      {state === "valid" && (
        <Form
          step={ERecovery.ThirdStep}
          buttonText={t('buttonText')}
          handleNextStep={handleNextStep}
          resetToken={resetToken}
        />
      )}

      {state === "invalid" && (
        <Stack width="100%" spacing="10px">
          <GradientButton
            isBigButton
            isFullWidth
            type="button"
            onClick={() => onRequestNewLink?.()}
          >
            Request a new link
          </GradientButton>
        </Stack>
      )}
    </Stack>
  );
};

export default ThirdStep;
